/**
 * Shared RTK Query API slice for driver–vehicle authorisation.
 *
 * A client restricts which vehicles a driver may drive; the VMS reporting the
 * driver in any other vehicle raises a bureau alert. Allow-by-default: a driver
 * with no rules is "unrestricted" and never checked. The feature is flagged per
 * organisation (see useDriverAuthorizationFeature).
 *
 * Shared across portals because the client portal edits the rules while the
 * admin portal renders them read-only. Every endpoint 404s when the relevant
 * organisation does not have the feature.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

const TAG = 'DriverAuthorization';
const CLIENT_SUMMARY_ID = 'CLIENT_SUMMARY';

const driverTag = (driverPk) => ({ type: TAG, id: `driver-${driverPk}` });
const orgTag = (orgId) => ({ type: TAG, id: `org-${orgId}` });

const driverAuthorizationUrl = (driverPk) => `fleet/client/drivers/${driverPk}/authorizations/`;

export const driverAuthorizationApiSlice = createApi({
  reducerPath: 'driverAuthorizationApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [TAG],
  endpoints: (builder) => ({
    // ── One driver's rules (client edit dialog) ─────────────────────────────
    // Response carries `can_edit` (false for bureau/admin staff) and, only when
    // editable, the group/vehicle `options` the user may pick from.
    fetchDriverAuthorization: builder.query({
      query: (driverPk) => driverAuthorizationUrl(driverPk),
      providesTags: (result, error, driverPk) => [driverTag(driverPk)],
    }),

    // Replace-the-set. 'unrestricted' ignores the id lists; 'restricted' with
    // both lists empty is a 400. Returns the same shape as the GET.
    saveDriverAuthorization: builder.mutation({
      query: ({ driverPk, mode, group_ids, vehicle_ids }) => ({
        url: driverAuthorizationUrl(driverPk),
        method: 'PUT',
        body: { mode, group_ids, vehicle_ids },
      }),
      // The driver tag is invalidated even on failure: a 409 (concurrent edit)
      // must refetch the fresh state rather than keep showing the stale one.
      invalidatesTags: (result, error, { driverPk }) => {
        const tags = [driverTag(driverPk), { type: TAG, id: CLIENT_SUMMARY_ID }];
        if (result?.organization_id != null) {
          tags.push(orgTag(result.organization_id));
        }
        return tags;
      },
    }),

    // ── Restricted drivers of the active organisation (client drivers page) ─
    // A driver absent from `drivers` is unrestricted.
    fetchClientDriverAuthorizationSummary: builder.query({
      query: () => 'fleet/client/driver-authorizations/',
      providesTags: [{ type: TAG, id: CLIENT_SUMMARY_ID }],
    }),

    // ── Every driver of an organisation (staff, read-only) ──────────────────
    fetchOrganizationDriverAuthorizations: builder.query({
      query: (orgId) => `fleet/organizations/${orgId}/driver-authorizations/`,
      providesTags: (result, error, orgId) => [orgTag(orgId)],
    }),
  }),
});

export const {
  useFetchDriverAuthorizationQuery,
  useSaveDriverAuthorizationMutation,
  useFetchClientDriverAuthorizationSummaryQuery,
  useFetchOrganizationDriverAuthorizationsQuery,
} = driverAuthorizationApiSlice;
